import {useCallback} from 'react';
import {Alert} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Article} from '../types/article';
import {Match} from '../types/diretta';
import {fetchMatchDetails} from '../services/diretta';

type RootStackParamList = {
  Home: undefined;
  Article: {article: Article; index: number; articles: Article[]};
  Diretta: {match: Match; initialTab?: string};
};

/**
 * Hook to handle article press with special navigation logic.
 * If article has match_id, pagelle_match_id, or formazione_match_id,
 * navigates to Diretta screen (with appropriate initial tab).
 * Otherwise navigates to Article screen.
 */
export const useArticleNavigation = (articles: Article[] = []) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const handleArticlePress = useCallback(
    async (article: Article, index: number) => {
      // Check for any match-related ID and navigate to Diretta screen
      // ONLY if the article is in the "Diretta" category
      const matchId = article.match_id || article.pagelle_match_id || article.formazione_match_id;
      const isDirettaCategory = article.category?.toLowerCase() === 'diretta';
      
      if (matchId && isDirettaCategory) {
        try {
          const matchData = await fetchMatchDetails(String(matchId));
          if (matchData) {
            // Determine which tab to open based on which ID was present
            let initialTab = 'cronaca'; // Default tab
            if (article.pagelle_match_id) {
              initialTab = 'pagelle';
            } else if (article.formazione_match_id) {
              initialTab = 'formazioni';
            }
            
            navigation.navigate('Diretta', {match: matchData, initialTab});
            return;
          }
        } catch {
          Alert.alert('Errore', 'Impossibile caricare i dati della partita');
        }
      }
      // Default: navigate to article (push to ensure new screen even if already on Article)
      navigation.push('Article', {article, index, articles});
    },
    [navigation, articles],
  );

  return {handleArticlePress};
};

// Helper to check if article has any match ID (for featured display logic)
export const hasMatchId = (article: Article): boolean => {
  return (
    (article.match_id !== null && article.match_id !== undefined) ||
    (article.pagelle_match_id !== null && article.pagelle_match_id !== undefined) ||
    (article.formazione_match_id !== null && article.formazione_match_id !== undefined)
  );
};
